/**
 * TanStack Query hooks for organization management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchOrganization,
  updateOrganization,
} from '../services/organizationApi'
import type { Organization, UpdateOrganizationData } from '../types'
import { useTranslation } from 'react-i18next'

/**
 * Hook for fetching organization details.
 */
export function useOrganization(
  id: number | null,
  options: { enabled?: boolean } = {},
) {
  const { t } = useTranslation('organizations')
  const enabled = options.enabled !== undefined ? options.enabled : true

  return useQuery<Organization, Error>({
    queryKey: ['organization', id],
    queryFn: ({ signal }) => {
      if (id === null) {
        throw new Error(t('useOrganization.organization_id_is_required'))
      }
      return fetchOrganization(id, { signal })
    },
    enabled: enabled && id !== null,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook for updating organization profile.
 */
export function useUpdateOrganization(organizationId: number) {
  const queryClient = useQueryClient()

  return useMutation<Organization, Error, UpdateOrganizationData>({
    mutationFn: (data) => updateOrganization(organizationId, data),
    onSuccess: () => {
      // Invalidate organization query to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['organization', organizationId] })
      // Also invalidate locations list in case organization name changed
      queryClient.invalidateQueries({ queryKey: ['organization', organizationId, 'locations'] })
    },
  })
}
